import ClassNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Chevron from 'components/Chevron';
import CloseIcon, { SIZES } from 'components/CloseIcon';
import IconButton from 'components/IconButton';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

import { TYPES } from './constants';

export { TYPES };

const Select = ({ type, children, value, ariaLabel, id, hasClear, onClear, ...otherProps }) => (
  <div className={ClassNames('Select', { [`Select--${type}`]: type })}>
    <select
      id={id}
      className={ClassNames('Select__select', {
        [`Select__select--${type}`]: type,
        'Select__select--has-clear': hasClear,
      })}
      value={value}
      aria-label={ariaLabel}
      {...otherProps}
    >
      {children}
    </select>
    {hasClear && value && (
      <IconButton
        className="Select__clear-button"
        onClick={onClear}
        aria-label={I18n.localize('global.clearSelect')}
      >
        <CloseIcon size={SIZES.SMALL} disableHoverAnimation />
      </IconButton>
    )}
    <div className={ClassNames('Select__chevron', { [`Select__chevron--${type}`]: type })}>
      <Chevron fillColor={type === TYPES.INVERSE ? 'colors-white' : 'colors-black'} />
    </div>
    <style jsx>
      {`
        .Select {
          position: relative;
          padding: 0;
        }

        .Select__select {
          width: 100%;
          appearance: none;
          outline: none;
          height: 48px;
          font-size: 14px;
          letter-spacing: 0.5px;
          font-weight: ${Theme.get('font-weight-normal')};
          background: ${Theme.get('colors-white')};
          border-bottom-color: ${Theme.get('colors-black')};
          border-width: 0;
          border-bottom-width: 2px;
          border-style: solid;
          border-radius: 0;
          padding: 2px ${Theme.get('spacing') * 8}px 0 ${Theme.get('spacing') * 2}px;
          color: ${Theme.get('colors-black')};
          box-shadow: 0 2px 0 0 inset transparent, 2px 0 0 0 inset transparent,
            -2px 0 0 0 inset transparent;
          transition: border-color ${Theme.get('transition-duration-ms')}}
              ${Theme.get('transition-timing-ease-out-quad')},
            background-color ${Theme.get('transition-duration-ms')}
              ${Theme.get('transition-timing-ease-out-quad')},
            box-shadow ${Theme.get('transition-duration-ms')}}
              ${Theme.get('transition-timing-ease-out-quad')};
          -webkit-tap-highlight-color: transparent;
        }

        .Select__select--has-clear {
          padding-right: ${Theme.get('spacing') * 10}px;
        }

        .Select__select:focus,
        .Select__select:active {
          box-shadow: 0 2px 0 0 inset ${Theme.get('colors-black')},
            2px 0 0 0 inset ${Theme.get('colors-black')},
            -2px 0 0 0 inset ${Theme.get('colors-black')};
        }

        .Select__select--${TYPES.INVERSE} {
          border-color: ${Theme.get('colors-white')};
          color: ${Theme.get('colors-white')};
          background-color: ${Theme.get('colors-black-80')};
        }

        .Select__select--${TYPES.INVERSE}:focus, .Select__select--${TYPES.INVERSE}:active {
          border-color: ${Theme.get('colors-white')};
          box-shadow: 0 2px 0 0 inset ${Theme.get('colors-white')},
            2px 0 0 0 inset ${Theme.get('colors-white')},
            -2px 0 0 0 inset ${Theme.get('colors-white')};
        }

        .Select__chevron {
          position: absolute;
          pointer-events: none;
          top: 50%;
          transform: translateY(-50%);
          right: ${Theme.get('spacing') * 3}px;
        }
      `}
    </style>
    <style jsx global>
      {`
        button.IconButton.Select__clear-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          right: ${Theme.get('spacing') * 6}px;
          padding: ${Theme.get('spacing') / 2}px;
          margin: 0;
          min-height: auto;
          min-width: auto;
        }
      `}
    </style>
  </div>
);

Select.propTypes = {
  type: PropTypes.oneOf(Object.values(TYPES)),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ariaLabel: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  hasClear: PropTypes.bool,
  onClear: PropTypes.func,
};

Select.defaultProps = {
  type: TYPES.DEFAULT,
  children: null,
  value: '',
  hasClear: false,
  onClear: () => {},
};

export default Select;
